import { graphql, useStaticQuery } from "gatsby"
import Img from "gatsby-image"
import React from "react"
import { css } from "styled-components"
import Spacer from "../../design-system/atoms/layout-primitives/spacer"
import VariantContainer from "../../design-system/atoms/variant-container"
import Grid from "../shared/grid"
import withAnimation, { FadeIn } from "../../design-system/util/withAnimation"
import ProjectDescription from "./project-description"
import {
  StatItem,
  StatItemDescription,
  StatItemNumber,
  StatRow,
} from "./project-description.style"
const AnimatedVariantContainer = withAnimation(VariantContainer)

export default function Projects() {
  const query = useStaticQuery(graphql`
    query {
      webAppFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/fade_titles/web.svg"
        }
      ) {
        publicURL
      }
      webAppImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/responsive_web.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      kioskFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/fade_titles/kiosk.svg"
        }
      ) {
        publicURL
      }
      kioskImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/directory.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      leasingFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/fade_titles/leasing.svg"
        }
      ) {
        publicURL
      }
      leasingImg: file(
        relativePath: {
          eq: "1_SOLUTIONS/Pre-Built Applications/Desktop/leasing.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 720, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  return (
    <>
      <Spacer height={{ sm: "32px", md: "64px", lg: "128px" }} />
      <Grid>
        {/* Responsive Web App */}
        <AnimatedVariantContainer
          animation={FadeIn}
          variants={[
            css`
              overflow: hidden;
              align-self: center;
              & > * {
                max-width: 100%;
                max-height: 100%;
              }
              grid-column: 2 / -2;
            `,
            css`
              grid-column: 3 / -3;
            `,
            css`
              grid-column: 1 / 7;
            `,
          ]}
        >
          <Img fluid={query.webAppImg.childImageSharp.fluid} alt="" />
        </AnimatedVariantContainer>
        <ProjectDescription
          fadeSettings={{ lg: "left" }}
          fadeImage={query.webAppFade.publicURL}
          fadeTitle="Responsive Web App"
          fadeAlt="The word 'web' displayed behind the title"
          containerVariants={[
            css`
              grid-column: 1 / -1;
            `,
            css`
              grid-column: 2/-2;
            `,
            css`
              grid-column: 8 / -1;
            `,
          ]}
          description={
            <>
              <span>Mappedin’s Responsive Web App</span> makes navigating
              complex properties seamless. With a mobile responsive design and
              indoor positioning capability, users can take directions with
              them, providing an end-to-end wayfinding experience.
            </>
          }
          buttonHref="/wayfinding/web-app/"
        >
          <StatRow>
            <StatItem>
              <StatItemNumber>638</StatItemNumber>
              <StatItemDescription>Global Properties</StatItemDescription>
            </StatItem>
            <StatItem>
              <StatItemNumber>
                2.8<span>M</span>
              </StatItemNumber>
              <StatItemDescription>Searches/Year</StatItemDescription>
            </StatItem>
            <StatItem>
              <StatItemNumber>
                39<div className="long">,000,000</div>
                <span className="short">M</span>
              </StatItemNumber>
              <StatItemDescription>
                Directions Served
                <span>/Year</span>
              </StatItemDescription>
            </StatItem>
          </StatRow>
        </ProjectDescription>
        {/* Digital Directory */}
        <Spacer height={{ sm: "64px", md: "96px", lg: "0px" }} />
        <AnimatedVariantContainer
          animation={FadeIn}
          variants={[
            css`
              overflow: hidden;
              align-self: center;
              & > * {
                max-width: 100%;
                max-height: 100%;
              }
              grid-column: 3 / -3;
            `,
            css`
              grid-column: 5 / -5;
            `,
            css`
              grid-column: 8 / -2;
              grid-row: 3;
              margin-top: 128px;
            `,
          ]}
        >
          <Img fluid={query.kioskImg.childImageSharp.fluid} alt="" />
        </AnimatedVariantContainer>
        <ProjectDescription
          fadeSettings={{ lg: "left" }}
          fadeImage={query.kioskFade.publicURL}
          fadeTitle="Digital Directory"
          fadeAlt="The word 'kiosk' displayed behind the title"
          containerVariants={[
            css`
              grid-column: 1 / -1;
            `,
            css`
              grid-column: 2/-2;
            `,
            css`
              grid-column: 2 / 7;
              grid-row: 3;
            `,
          ]}
          buttonHref="https://www.mappedin.com/wayfinding/digital-directory/"
          description={
            <>
              <span>Mappedin’s Digital Directory</span> is full of features
              including smart search, multi-floor navigation, deals and
              promotions, mobile pass, and accessibility controls. With millions
              of searches per month, our directory serves a funtional purpose
              for users as well as an advertising and experiential content
              platform for our customers.
            </>
          }
        >
          <StatRow>
            <StatItem>
              <StatItemNumber>253</StatItemNumber>
              <StatItemDescription>Global Properties</StatItemDescription>
            </StatItem>
            <StatItem>
              <StatItemNumber>2,435</StatItemNumber>
              <StatItemDescription>Directory Screens</StatItemDescription>
            </StatItem>
            <StatItem>
              <StatItemNumber>
                22<div>,000,000</div>
                <span className="short">M</span>
              </StatItemNumber>
              <StatItemDescription>
                Avg. Sessions<span>/Year</span>
              </StatItemDescription>
            </StatItem>
          </StatRow>
        </ProjectDescription>
      </Grid>
      <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
    </>
  )
}
